import React, { useRef, useEffect } from 'react';
import { useLatest } from 'ahooks';

export default function useIntersectionLoadMore(props: {
  container?: React.RefObject<HTMLElement | null>;
  loadingContainer?: React.RefObject<HTMLElement | null>;
  threshold?: IntersectionObserverInit['threshold'];
  deps?: React.DependencyList;
  onLoadMore?: (stopObserver: () => void) => void;
}) {
  const { container, loadingContainer, deps, threshold = 0.5, onLoadMore } = props;

  const observer = useRef<IntersectionObserver>();

  const onLoadMoreLatest = useLatest(onLoadMore);

  useEffect(() => {
    if (!container?.current || !loadingContainer?.current) return;

    const stop = () => {
      if (loadingContainer?.current) {
        observer.current?.unobserve(loadingContainer?.current);
      }
      observer.current?.disconnect();
    };

    const callback = (intersections?: IntersectionObserverEntry[]) => {
      if (!Array.isArray(intersections) || intersections[0]?.intersectionRatio <= 0) return;
      onLoadMoreLatest.current?.(stop);
    };

    observer.current = new IntersectionObserver(callback, {
      root: container?.current,
      threshold,
    });

    observer.current.observe(loadingContainer?.current);

    return () => {
      callback(observer.current?.takeRecords());
      stop();
    };
  }, [deps]);
}
